import { type FC, type FormEvent, useCallback, useEffect, useId, useMemo, useState } from "react";
import {
    type PlainProject,
    useCreateProjectPolicy,
    useDeleteProjectPolicy,
    useUpdateProject,
    useListProjectEnvironmentClasses,
    useUpdateProjectEnvironmentClasses,
    useProjectState,
} from "@/queries/project-queries";
import { ContextUrlInput } from "@/components/ContextUrlInput";
import { Input } from "@/components/podkit/forms/Input";
import {
    ProjectPrebuildConfiguration,
    PrebuildTrigger,
    PrebuildTrigger_DailySchedule,
} from "gitpod-next-api/gitpod/v1/project_pb";

import {
    convertToProjectEnvironmentClass,
    type EnvironmentClassEntry,
    useEnvironmentClassEntries,
} from "@/hooks/use-environment-class-entries";
import {
    type ContextURLInitializer,
    type EnvironmentInitializer,
    type GitInitializer,
} from "gitpod-next-api/gitpod/v1/environment_pb";
import { useToast } from "@/components/podkit/toasts/use-toast";
import { formatError } from "@/utils/errors";
import { InputField } from "@/components/podkit/forms/InputField";
import { Label } from "@/components/podkit/forms/Label";
import type { PlainMessage } from "@bufbuild/protobuf";
import { equals } from "@/utils/objects";

import { equals as arrayEquals } from "@/utils/arrays";
import { Button } from "@/components/flexkit/Button";
import { localEnvType } from "@/utils/environment-classes";
import { EnvironmentClassOrderedList } from "@/components/EnvironmentClassOrderedList";
import { useResourcePermissions } from "@/hooks/use-resource-permissions";
import { useFlagGroupManagementEnabled, useFlagPrebuildsJetbrainsWarmupEnabled } from "@/hooks/use-flag";
import { ResourceType } from "gitpod-next-api/gitpod/v1/event_pb";
import { cn } from "@/components/podkit/lib/cn";
import { Switch } from "@/components/Switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/podkit/select/Select";
import { Text } from "@/components/podkit/typography/Text";
import { PrebuildExecutorSelect } from "@/components/projects/PrebuildExecutorSelect";
import { Principal, type Subject } from "gitpod-next-api/gitpod/v1/identity_pb";
import { Tooltip } from "@/components/Tooltip";
import { IconInfo } from "@/assets/icons/geist/IconInfo";
import { useAuthenticatedUser } from "@/queries/user-queries";

export const ProjectSettings: FC<{
    project: PlainProject;
}> = ({ project }) => {
    const [projectName, setProjectName] = useState<string>(project.metadata?.name || "");
    const inputNameID = useId();
    const inputDevContainerFilePathID = useId();
    const inputAutomationsFilePath = useId();
    const inputPrebuildTimeID = useId();

    const [initializer, setInitializer] = useState<PlainMessage<EnvironmentInitializer> | undefined>(
        project.initializer,
    );

    // Calculate default prebuild hour: 6AM in user's local timezone, converted to UTC
    // This spreads defaults across timezones instead of everyone defaulting to 6 UTC
    const prebuildDefaultHour = useMemo(() => {
        const localHour = 6;
        const offsetMinutes = new Date().getTimezoneOffset();
        // getTimezoneOffset returns minutes behind UTC (e.g., UTC-5 returns 300)
        // So UTC hour = local hour + offset/60
        const utcHour = (localHour + offsetMinutes / 60 + 24) % 24;
        return Math.floor(utcHour);
    }, []);

    // Prebuild configuration state
    const [prebuildEnabled, setPrebuildEnabled] = useState<boolean>(project.prebuildConfiguration?.enabled || false);
    const [prebuildHourUtc, setPrebuildHourUtc] = useState<number>(
        project.prebuildConfiguration?.trigger?.trigger?.case === "dailySchedule"
            ? project.prebuildConfiguration.trigger.trigger.value.hourUtc ?? prebuildDefaultHour
            : prebuildDefaultHour,
    );
    const [prebuildEnvironmentClassIds, setPrebuildEnvironmentClassIds] = useState<string[]>(
        project.prebuildConfiguration?.environmentClassIds || [],
    );
    const [prebuildExecutor, setPrebuildExecutor] = useState<PlainMessage<Subject> | undefined>(
        project.prebuildConfiguration?.executor,
    );
    // Track if the user explicitly changed the executor (to avoid sending unchanged default values)
    const [prebuildExecutorExplicitlyChanged, setPrebuildExecutorExplicitlyChanged] = useState(false);
    const inputPrebuildExecutorID = useId();
    const [enableJetbrainsWarmup, setEnableJetbrainsWarmup] = useState<boolean>(
        project.prebuildConfiguration?.enableJetbrainsWarmup || false,
    );

    const { data: projectEnvironmentClassesData, isLoading: isLoadingProjectEnvClasses } =
        useListProjectEnvironmentClasses(project.id);

    const { environmentClassEntries: unfilteredEnvironmentClassEntries, isLoading: isLoadingEnvClasses } =
        useEnvironmentClassEntries();
    const [selectedEnvironmentClassEntries, setSelectedEnvironmentClassEntries] = useState<EnvironmentClassEntry[]>();

    const { value: isGroupManagementEnabled } = useFlagGroupManagementEnabled();
    const { value: isJetbrainsWarmupEnabled } = useFlagPrebuildsJetbrainsWarmupEnabled();
    const { hasPermission } = useResourcePermissions({ enabled: isGroupManagementEnabled });
    const { data: projectState } = useProjectState(project.id);
    const { data: currentUser } = useAuthenticatedUser();

    // The effective executor to display: configured executor or current user as default
    const effectiveExecutor = useMemo((): PlainMessage<Subject> | undefined => {
        if (prebuildExecutor) {
            return prebuildExecutor;
        }
        if (currentUser) {
            return { id: currentUser.id, principal: Principal.USER };
        }
        return undefined;
    }, [prebuildExecutor, currentUser]);

    // When flag is OFF: if you can view the project, you can edit it (old behavior)
    // When flag is ON: check org-wide sharing OR group-based permissions
    //   - If shared org-wide: everyone in org can edit (old behavior)
    //   - Otherwise: check project:update permission (supports read-only access via PROJECT_USER role)
    const isSharedOrgWide = projectState?.shared === true;
    const canUpdateProject = isGroupManagementEnabled
        ? isSharedOrgWide || hasPermission(ResourceType.PROJECT, project.id, "project:update")
        : true;

    const allEnvironmentClassEntries = useMemo(() => {
        const localRunner = unfilteredEnvironmentClassEntries.find((e) => e.localRunner);

        // filter out local runners of the current user
        const filtered = unfilteredEnvironmentClassEntries.filter((e) => !e.localRunner);

        // re-add a synthetic local runner
        if (localRunner) {
            localEnvType.policyRestricted = localRunner.policyRestricted;
            return [localEnvType, ...filtered];
        }
        return filtered;
    }, [unfilteredEnvironmentClassEntries]);

    const [devcontainerFilePath, setDevcontainerFilePath] = useState<string>(project.devcontainerFilePath);
    const [devcontainerFilePathError, setDevcontainerFilePathError] = useState<string>("");

    // Validation function for devcontainer file path
    // Based on https://github.com/devcontainers/cli/blob/721c21b8fba699679cc03c01baa782e816be6f54/src/spec-node/configContainer.ts#L28
    const validateDevcontainerFilePath = useCallback((path: string): string => {
        if (!path.trim()) {
            return ""; // Empty path is valid (auto-discovery)
        }

        // Check if filename ends with devcontainer.json or .devcontainer.json
        // Adapted from devcontainer CLI validation for relative paths:
        // https://github.com/devcontainers/cli/blob/721c21b8fba699679cc03c01baa782e816be6f54/src/spec-node/configContainer.ts#L27
        // Modified to handle relative paths (no leading slash required)
        //
        // Regex breakdown: /(?:^|\/)\.?devcontainer\.json$/
        // (?:^|\/) - non-capturing group that matches either:
        //   ^ - start of string (for root-level files like "devcontainer.json"), OR
        //   \/ - literal forward slash (for nested paths like "path/devcontainer.json")
        // \.? - optional literal dot (matches both "devcontainer.json" and ".devcontainer.json")
        // devcontainer\.json - literal "devcontainer.json"
        // $ - end of string
        if (!/(?:^|\/)\.?devcontainer\.json$/.test(path)) {
            return "Filename must be devcontainer.json or .devcontainer.json";
        }

        return "";
    }, []);

    // Validate initial devcontainer file path
    useEffect(() => {
        const error = validateDevcontainerFilePath(project.devcontainerFilePath);
        setDevcontainerFilePathError(error);
    }, [project.devcontainerFilePath, validateDevcontainerFilePath]);

    const [automationsFilePath, setAutomationsFilePath] = useState<string>(project.automationsFilePath);

    const { toast } = useToast();

    const updateProject = useUpdateProject();
    const createProjectPolicy = useCreateProjectPolicy();
    const deleteProjectPolicy = useDeleteProjectPolicy();
    const updateProjectEnvironmentClasses = useUpdateProjectEnvironmentClasses();

    const projectEnvironmentClassesChanged = useMemo(() => {
        return (
            !!projectEnvironmentClassesData &&
            !!selectedEnvironmentClassEntries &&
            !arrayEquals(
                projectEnvironmentClassesData.environmentClasses,
                selectedEnvironmentClassEntries.map((v, i) => convertToProjectEnvironmentClass(v, i)),
                (a, b) =>
                    a.environmentClass.case === b.environmentClass.case &&
                    a.environmentClass.value === b.environmentClass.value,
            )
        );
    }, [projectEnvironmentClassesData, selectedEnvironmentClassEntries]);

    const prebuildExecutorChanged = prebuildExecutorExplicitlyChanged;

    const handlePrebuildExecutorChange = useCallback((executor: PlainMessage<Subject> | undefined) => {
        setPrebuildExecutor(executor);
        setPrebuildExecutorExplicitlyChanged(true);
    }, []);

    const prebuildConfigChanged =
        prebuildEnabled !== (project.prebuildConfiguration?.enabled || false) ||
        prebuildHourUtc !==
            ((project.prebuildConfiguration?.trigger?.trigger?.case === "dailySchedule"
                ? project.prebuildConfiguration.trigger.trigger.value.hourUtc
                : undefined) || prebuildDefaultHour) ||
        !arrayEquals(
            prebuildEnvironmentClassIds,
            project.prebuildConfiguration?.environmentClassIds || [],
            (a, b) => a === b,
        ) ||
        prebuildExecutorChanged ||
        enableJetbrainsWarmup !== (project.prebuildConfiguration?.enableJetbrainsWarmup || false);

    const formChanged =
        projectName !== project.metadata?.name ||
        !equals(project.initializer, initializer) ||
        devcontainerFilePath !== project.devcontainerFilePath ||
        automationsFilePath !== project.automationsFilePath ||
        projectEnvironmentClassesChanged ||
        prebuildConfigChanged;

    const handleSubmit = useCallback(
        async (event: FormEvent<HTMLFormElement>) => {
            event.preventDefault();

            if (!projectName || !initializer || !selectedEnvironmentClassEntries) {
                console.log(`Invalid project Name${projectName}  or initializer`);
                return;
            }

            try {
                // Build prebuild configuration if changed
                let prebuildConfiguration: ProjectPrebuildConfiguration | undefined;
                if (prebuildConfigChanged) {
                    // Determine executor to send. API requires executor when prebuilds are enabled.
                    // Priority: explicit user selection > existing config > current user as default
                    let executorToSend: PlainMessage<Subject> | undefined;
                    if (prebuildEnabled) {
                        if (prebuildExecutorExplicitlyChanged) {
                            executorToSend = prebuildExecutor;
                        } else if (project.prebuildConfiguration?.executor) {
                            executorToSend = project.prebuildConfiguration.executor;
                        } else if (currentUser) {
                            // Default to current user when enabling prebuilds for the first time
                            executorToSend = { id: currentUser.id, principal: Principal.USER };
                        }
                    }

                    prebuildConfiguration = new ProjectPrebuildConfiguration({
                        enabled: prebuildEnabled,
                        environmentClassIds: prebuildEnabled ? prebuildEnvironmentClassIds : [],
                        trigger: prebuildEnabled
                            ? new PrebuildTrigger({
                                  trigger: {
                                      case: "dailySchedule",
                                      value: new PrebuildTrigger_DailySchedule({
                                          hourUtc: prebuildHourUtc,
                                      }),
                                  },
                              })
                            : undefined,
                        timeout: project.prebuildConfiguration?.timeout || undefined,
                        executor: executorToSend,
                        enableJetbrainsWarmup: prebuildEnabled ? enableJetbrainsWarmup : false,
                    });
                }

                // Track if prebuilds are being enabled for the first time
                const enablingPrebuilds =
                    prebuildConfigChanged && prebuildEnabled && !project.prebuildConfiguration?.enabled;

                // Update basic project settings
                await updateProject.mutateAsync({
                    projectId: project.id,
                    name: projectName !== project.metadata?.name ? projectName : undefined,
                    initializer: !equals(project.initializer, initializer) ? initializer : undefined,
                    devcontainerFilePath:
                        devcontainerFilePath !== project.devcontainerFilePath ? devcontainerFilePath : undefined,
                    automationsFilePath:
                        automationsFilePath !== project.automationsFilePath ? automationsFilePath : undefined,
                    prebuildConfiguration,
                });

                // Update project environment classes if they have changed
                if (selectedEnvironmentClassEntries.length > 0 && projectEnvironmentClassesChanged) {
                    await updateProjectEnvironmentClasses.mutateAsync({
                        projectId: project.id,
                        projectEnvironmentClasses: selectedEnvironmentClassEntries.map((entry, index) =>
                            convertToProjectEnvironmentClass(entry, index),
                        ),
                    });
                }

                // Show custom success message when enabling prebuilds
                if (enablingPrebuilds) {
                    toast({
                        title: `Successfully enabled Prebuild for project '${projectName}'!`,
                    });
                } else {
                    toast({
                        title: "Project updated",
                    });
                }
            } catch (error) {
                toast({
                    title: "Failed to update project",
                    description: formatError(error),
                });
            }
        },
        [
            projectName,
            updateProject,
            project,
            devcontainerFilePath,
            automationsFilePath,
            toast,
            initializer,
            selectedEnvironmentClassEntries,
            updateProjectEnvironmentClasses,
            projectEnvironmentClassesChanged,
            prebuildConfigChanged,
            prebuildEnabled,
            prebuildEnvironmentClassIds,
            prebuildHourUtc,
            prebuildExecutor,
            prebuildExecutorExplicitlyChanged,
            currentUser,
            enableJetbrainsWarmup,
        ],
    );

    useEffect(() => {
        if (
            !selectedEnvironmentClassEntries &&
            projectEnvironmentClassesData?.environmentClasses &&
            !isLoadingEnvClasses
        ) {
            setSelectedEnvironmentClassEntries(
                projectEnvironmentClassesData.environmentClasses
                    .map((v) => {
                        if (v.environmentClass.case === "localRunner") {
                            return localEnvType;
                        }

                        return allEnvironmentClassEntries.find((e) => e.clazz.id === v.environmentClass.value);
                    })
                    .filter((v) => !!v),
            );
        }
    }, [
        project,
        allEnvironmentClassEntries,
        projectEnvironmentClassesData,
        selectedEnvironmentClassEntries,
        isLoadingEnvClasses,
    ]);

    const updateButtonDisabled =
        !projectEnvironmentClassesData ||
        !projectName ||
        !initializer ||
        !formChanged ||
        !selectedEnvironmentClassEntries ||
        selectedEnvironmentClassEntries.length === 0 ||
        !!devcontainerFilePathError; // Disable if there's a validation error

    const updateButtonLoading =
        updateProject.isPending ||
        createProjectPolicy.isPending ||
        deleteProjectPolicy.isPending ||
        updateProjectEnvironmentClasses.isPending;

    const metadataFields = (
        <>
            <InputField label="Display name" id={inputNameID}>
                <Input
                    data-testid="project-name-input"
                    id={inputNameID}
                    type="text"
                    value={projectName || ""}
                    placeholder="Ex: My Project"
                    disabled={!canUpdateProject}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        setProjectName(e.target.value.trimStart().replace(/\s+/g, " "))
                    }
                />
            </InputField>
            <ProjectInitializerFields
                initializer={project?.initializer}
                onUpdate={(v) => {
                    setInitializer(v);
                }}
                onError={() => setInitializer(undefined)}
                disabled={!canUpdateProject}
            />
        </>
    );

    const envClassFields = (
        <EnvironmentClassOrderedList
            label="Environment Classes"
            environmentClassEntries={selectedEnvironmentClassEntries}
            allEnvironmentClassEntries={allEnvironmentClassEntries}
            onChange={setSelectedEnvironmentClassEntries}
            loading={isLoadingEnvClasses || isLoadingProjectEnvClasses}
            disabled={!canUpdateProject}
            minItems={1}
            maxItems={30}
            showPrebuildColumn={prebuildEnabled}
            prebuildSelectedIds={prebuildEnvironmentClassIds}
            onPrebuildSelectionChange={setPrebuildEnvironmentClassIds}
        />
    );

    const devcontainerFields = (
        <InputField
            label="Dev Container file path"
            id={inputDevContainerFilePathID}
            hint={"Example: .devcontainer/devcontainer.json"}
            error={devcontainerFilePathError}
        >
            <Input
                id={inputDevContainerFilePathID}
                type="text"
                value={devcontainerFilePath}
                disabled={!canUpdateProject}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    const newValue = e.target.value;
                    setDevcontainerFilePath(newValue);

                    // Validate the path and set error state
                    const error = validateDevcontainerFilePath(newValue);
                    setDevcontainerFilePathError(error);
                }}
                placeholder="default: auto discovered"
                data-testid="project-dev-container-path"
            />
        </InputField>
    );

    const automationFields = (
        <InputField label="Automations file path" id={inputAutomationsFilePath} hint="Example: .ona/automations.yaml">
            <Input
                id={inputAutomationsFilePath}
                type="text"
                value={automationsFilePath}
                disabled={!canUpdateProject}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setAutomationsFilePath(e.target.value)}
                placeholder="default: .ona/automations.yaml"
                data-testid="project-automations-path"
            />
        </InputField>
    );

    const handlePrebuildToggle = useCallback(
        (value: boolean) => {
            setPrebuildEnabled(value);
            if (value) {
                if (prebuildEnvironmentClassIds.length === 0 && selectedEnvironmentClassEntries?.length) {
                    const firstEligible = selectedEnvironmentClassEntries.find(
                        (entry) => !!entry.clazz?.id && !entry.policyRestricted && !entry.localRunner,
                    );
                    const id = firstEligible?.clazz?.id;
                    if (id) {
                        setPrebuildEnvironmentClassIds([id]);
                    }
                }
            }
        },
        [prebuildEnvironmentClassIds.length, selectedEnvironmentClassEntries],
    );

    // Fallback: if prebuilds are enabled and ids are empty when entries load, auto-select first eligible
    useEffect(() => {
        if (
            prebuildEnabled &&
            prebuildEnvironmentClassIds.length === 0 &&
            selectedEnvironmentClassEntries &&
            selectedEnvironmentClassEntries.length > 0
        ) {
            const firstEligible = selectedEnvironmentClassEntries.find(
                (entry) => !!entry.clazz?.id && !entry.policyRestricted && !entry.localRunner,
            );
            const id = firstEligible?.clazz?.id;
            if (id) {
                setPrebuildEnvironmentClassIds([id]);
            }
        }
    }, [prebuildEnabled, prebuildEnvironmentClassIds.length, selectedEnvironmentClassEntries]);

    const prebuildFields = (
        <div className="rounded-lg border border-border-base p-4">
            <div className="flex flex-col gap-4">
                <div className="flex gap-4">
                    <div className="flex">
                        <Switch
                            id="prebuild-enabled-switch"
                            state={prebuildEnabled ? "checked" : "unchecked"}
                            onToggle={handlePrebuildToggle}
                            disabled={!canUpdateProject}
                            aria-label="Enable prebuild environments"
                            data-testid="prebuild-enabled-toggle"
                        />
                    </div>
                    <div className="flex flex-col gap-1">
                        <Text className="text-base font-medium">Prebuild Environments</Text>
                        <Text className="text-sm text-content-secondary">
                            Faster starts, no surprises. Ona builds environments in the background so they&apos;re ready
                            when you are.
                        </Text>
                        {prebuildEnabled && (
                            <div className="flex flex-col gap-3 pt-2">
                                <div className="flex items-center gap-2">
                                    <Label htmlFor={inputPrebuildTimeID} className="w-24 shrink-0">
                                        Runs daily at
                                    </Label>
                                    <Select
                                        value={prebuildHourUtc.toString()}
                                        onValueChange={(value) => setPrebuildHourUtc(parseInt(value, 10))}
                                        disabled={!canUpdateProject}
                                    >
                                        <SelectTrigger id={inputPrebuildTimeID} loading={false} className="w-24">
                                            <SelectValue placeholder="Select time" />
                                        </SelectTrigger>
                                        <SelectContent className="w-24">
                                            {Array.from({ length: 24 }, (_, i) => i).map((hour) => (
                                                <SelectItem key={hour} value={hour.toString()}>
                                                    <span>{hour.toString().padStart(2, "0")}:00</span>
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                    <span className="text-sm text-content-tertiary">UTC</span>
                                </div>
                                <div className="flex items-center gap-2">
                                    <Label htmlFor={inputPrebuildExecutorID} className="w-24 shrink-0">
                                        Run as
                                    </Label>
                                    {effectiveExecutor && (
                                        <PrebuildExecutorSelect
                                            selectedExecutor={effectiveExecutor}
                                            onExecutorChange={handlePrebuildExecutorChange}
                                            disabled={!canUpdateProject}
                                            className="w-[200px]"
                                        />
                                    )}
                                    <Tooltip content="Prebuilds use this identity's credentials to access the repository. User secrets are not included.">
                                        <span className="text-content-secondary">
                                            <IconInfo size="sm" />
                                        </span>
                                    </Tooltip>
                                </div>
                                {isJetbrainsWarmupEnabled && (
                                    <div className="flex gap-4 border-t border-border-base pt-4">
                                        <div className="flex">
                                            <Switch
                                                id="jetbrains-warmup-switch"
                                                state={enableJetbrainsWarmup ? "checked" : "unchecked"}
                                                onToggle={setEnableJetbrainsWarmup}
                                                disabled={!canUpdateProject}
                                                aria-label="Enable JetBrains editors warmup"
                                                data-testid="jetbrains-warmup-toggle"
                                            />
                                        </div>
                                        <div className="flex flex-col gap-1">
                                            <Text className="text-base font-medium">JetBrains editors warmup</Text>
                                            <Text className="text-sm text-content-secondary">
                                                Pre-configure remote JetBrains editors and build project indexes during
                                                prebuilds for faster editor startup.
                                            </Text>
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );

    return (
        <form onSubmit={handleSubmit} className="flex max-w-[456px] flex-col gap-6" data-testid="project-details-form">
            {metadataFields}
            {prebuildFields}
            {envClassFields}
            {devcontainerFields}
            {automationFields}
            <Button
                className={cn("max-w-fit", { hidden: !canUpdateProject })}
                type="submit"
                variant="primary"
                loading={updateButtonLoading}
                disabled={updateButtonDisabled}
                data-testid="save-changes-button"
                data-tracking-id="save-changes-project-settings"
            >
                Save Changes
            </Button>
        </form>
    );
};

type InitializerProps = {
    initializer?: PlainMessage<EnvironmentInitializer>;
    onUpdate: (initializer?: PlainMessage<EnvironmentInitializer>) => void;
    onError: (error: string) => void;
    disabled?: boolean;
};

const ProjectInitializerFields: React.FC<InitializerProps> = ({ initializer, onUpdate, onError, disabled = false }) => {
    switch (initializer?.specs[0].spec.case) {
        case "contextUrl":
            return (
                <ContextURLInputField
                    initializer={initializer?.specs[0].spec.value}
                    onUpdate={(updatedValue) =>
                        onUpdate({ specs: [{ spec: { value: updatedValue, case: "contextUrl" } }] })
                    }
                    onError={onError}
                    disabled={disabled}
                />
            );
        case "git":
            return (
                <GitInitializerInput
                    initializer={initializer?.specs[0].spec.value}
                    onUpdate={(updatedValue) => onUpdate({ specs: [{ spec: { value: updatedValue, case: "git" } }] })}
                    onError={onError}
                    disabled={disabled}
                />
            );
        default:
            return <></>;
    }
};

const ContextURLInputField: React.FC<{
    initializer: PlainMessage<ContextURLInitializer>;
    onUpdate: (initializer: PlainMessage<ContextURLInitializer>) => void;
    onError: (error: string) => void;
    disabled?: boolean;
}> = ({ initializer, onUpdate, onError, disabled = false }) => {
    const [contextURL, setContextURL] = useState(initializer.url);

    const handleChange = useCallback(
        (newURL: string) => {
            setContextURL(newURL);

            if (!newURL) {
                onError("Repository URL is required");
                return;
            }
            try {
                new URL(newURL);
            } catch {
                onError("Invalid Repository URL");
                return;
            }

            onUpdate({ url: newURL });
        },
        [onUpdate, onError],
    );

    return (
        <ContextUrlInput label="Context URL" onChange={(v) => handleChange(v)} value={contextURL} disabled={disabled} />
    );
};

const GitInitializerInput: React.FC<{
    initializer: PlainMessage<GitInitializer>;
    onUpdate: (initializer: PlainMessage<GitInitializer>) => void;
    onError: (error: string) => void;
    disabled?: boolean;
}> = ({ initializer, onUpdate, onError, disabled = false }) => {
    const inputRepoUrlID = useId();
    const inputBranchID = useId();

    const [branch, setBranch] = useState<string>(initializer.cloneTarget);
    const [repoURL, setRepoURL] = useState<string>(initializer.remoteUri);

    const handleChange = useCallback(
        (newRepoURL: string, newBranch: string) => {
            setRepoURL(newRepoURL);
            setBranch(newBranch);

            if (!newRepoURL) {
                onError("Repository URL is required");
                return;
            }
            if (!newBranch) {
                onError("Branch is required");
                return;
            }
            try {
                new URL(newRepoURL);
            } catch {
                onError("Invalid Repository URL");
                return;
            }

            const value = {
                ...initializer,
                cloneTarget: newBranch,
                remoteUri: newRepoURL,
            };
            onUpdate(value);
        },
        [initializer, onUpdate, onError],
    );

    return (
        <div className="flex flex-col gap-2">
            <InputField label="Repository Clone URL" id={inputRepoUrlID}>
                <Input
                    id={inputRepoUrlID}
                    type="text"
                    value={repoURL}
                    disabled={disabled}
                    onChange={(e) => handleChange(e.target.value.trimStart().replace(/\s+/g, " "), branch)}
                />
            </InputField>
            <InputField label="Branch" id={inputBranchID}>
                <Input
                    id={inputBranchID}
                    type="text"
                    value={branch}
                    disabled={disabled}
                    onChange={(e) => handleChange(repoURL, e.target.value.trimStart().replace(/\s+/g, " "))}
                />
            </InputField>
        </div>
    );
};
